import { useMemo, useState } from "react";
import { Flex, Card, Avatar, Button } from "antd";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { dashboardQueryFactory } from "@/console/apis/dashboard/queryFactory";
import { memberQueryFactory } from "@/console/apis/member/queryFactory";
import { managerQueryFactory } from "@/console/apis/manager/queryFactory";
import { LEVEL_TEXT, type Level } from "@/console/components/Level";
enum NotificationType {
  TEXT = 1,
  MEMBER_JOINED = 2,
  MEMBER_PROMOTED = 3,
  MEMBER_JOINED_ANNIVERSARY = 4,
  MEMBER_BIRTHDAY = 5,
  MANAGER_ADDED = 6,
  GYM_INFO_UPDATED = 7,
}
interface Notification {
  type: NotificationType;
  date: string;
  data?: {
    member_id?: number;
    manager_id?: number;
    level?: number;
  };
}

const Notifications = () => {
  const { t } = useTranslation();
  const [displayCount, setDisplayCount] = useState(5);
  const { data: notifications = [] } =
    dashboardQueryFactory.getNotificationList();

  const userIds = useMemo(() => {
    return [
      ...new Set(
        notifications
          .map((item) => {
            if (item.data?.member_id) return `member-${item.data.member_id}`;
            if (item.data?.manager_id) return `manager-${item.data.manager_id}`;
            return null;
          })
          .filter((id): id is string => id !== null)
      ),
    ];
  }, [notifications]);

  const userQueries = useQueries({
    queries: userIds.map((id) => {
      const [type, idNumber] = id.split("-");
      return {
        ...(type === "member"
          ? memberQueryFactory.getMemberDetail(Number(idNumber))
          : managerQueryFactory.getManagerById(Number(idNumber))),
        enabled: !!idNumber,
      };
    }),
  });

  const userDetailsMap = useMemo(() => {
    return new Map(
      userQueries
        .filter(
          (query): query is UseQueryResult<Member, Error> & { data: Member } =>
            !!query.data
        )
        .map((query) => {
          const id = query.data.id;
          const type = query.data.join_date ? "member" : "manager";
          return [`${type}-${id}`, query.data];
        })
    );
  }, [userQueries]);

  const getMessage = (item: Notification) => {
    const userKey = item.data?.member_id
      ? `member-${item.data.member_id}`
      : item.data?.manager_id
      ? `manager-${item.data.manager_id}`
      : undefined;

    if (item.type === NotificationType.GYM_INFO_UPDATED) {
      return t("notifications.messages.gymUpdate");
    }

    const userDetail = userKey ? userDetailsMap.get(userKey) : undefined;

    if (!userDetail) return t("notifications.userUnavailable");

    const userName = `${userDetail.first_name} ${userDetail.last_name}`;

    switch (item.type) {
      case 1:
        return t("notifications.messages.newMessage");
      case 2:
        return t("notifications.messages.newMember", { userName });
      case 3:
        return t("notifications.messages.memberAdvanced", {
          userName,
          level: LEVEL_TEXT[item.data?.level as Level],
        });
      case 4: {
        const joinDate = new Date(userDetail.join_date);
        const currentYear = new Date().getFullYear();
        const yearsOfMembership = currentYear - joinDate.getFullYear();
        return t("notifications.messages.memberAnniversary", {
          userName,
          years: yearsOfMembership,
        });
      }
      case 5:
        return t("notifications.messages.memberBirthday", { userName });
      case 6:
        return t("notifications.messages.managerInvitation", {
          email: userDetail.email,
        });
      default:
        return t("notifications.messages.unknown");
    }
  };

  const displayedNotifications = notifications.slice(0, displayCount);

  if (!notifications.length) return null;

  return (
    <Card
      title={t("notifications.title")}
      className="min-h-[260px] mt-4"
      styles={{
        body: {
          padding: "0px 24px 24px 24px",
        },
      }}
    >
      {displayedNotifications.map((item) => {
        const userKey = item.data?.member_id
          ? `member-${item.data.member_id}`
          : item.data?.manager_id
          ? `manager-${item.data.manager_id}`
          : undefined;

        return (
          <Flex
            key={`${item.type}-${item.date}`}
            className="border-b py-[7px]"
            align="center"
            justify="space-between"
          >
            <Flex align="center" style={{ flex: 1 }}>
              <Flex vertical className="ml-2">
                <span className="text-[12px] text-[#666]">{item.date}</span>
              </Flex>
              {userKey && (
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  src={userDetailsMap.get(userKey)?.profile_image_url}
                  className="ml-4"
                />
              )}
              <span className="ml-4">{getMessage(item as Notification)}</span>
            </Flex>
          </Flex>
        );
      })}

      {displayCount < notifications.length && (
        <Flex justify="center" className="mt-4">
          <Button
            type="link"
            onClick={() => setDisplayCount((prev) => prev + 5)}
          >
            {t("notifications.showMore")}
          </Button>
        </Flex>
      )}
    </Card>
  );
};

export default Notifications;
